// frontend/src/components/TestInterface.jsx
import React, { useState, useEffect, useRef } from 'react';
import { showWarning, showInfo, showError } from '../utils/toastUtils';
import 'react-toastify/dist/ReactToastify.css';
import QuestionNav from './QuestionNav';
import {
    saveResponse,
    addBlurEvent,
    saveProgress as saveProgressAPI,
    saveVideoSelection,
    getVideoSelections
} from '../services/api';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/outline';
import VideoQuestion from './VideoQuestion';
import { getTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext';

const TestInterface = ({
    accessToken,
    items,
    config,
    onSubmit,
    isCPM = false,
    assessmentType = 'MIPS',
    renderCPMImage = null,
    renderCPMOptions = null,
    savedProgress = null,
    onMiddlePhotoNeeded = null,
    facePhoto2Taken = false,
    language: languageProp = null,   // optional prop override
}) => {
    // Use global language from context; fall back to prop if provided
    const { language: ctxLanguage } = useLanguage();
    const language = languageProp || ctxLanguage || 'en';
    const t = (key, replacements) => getTranslation(language, key, replacements);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [blurCount, setBlurCount] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveStatus, setSaveStatus] = useState({});
    const [selectedOption, setSelectedOption] = useState(null);
    const [isResumed, setIsResumed] = useState(false);
    const [completedItems, setCompletedItems] = useState([]);
    const [imageLoaded, setImageLoaded] = useState(false);

    const pendingSaves = useRef(new Map());
    const saveTimeout = useRef(null);
    const containerRef = useRef(null);
    const progressSaveTimeout = useRef(null);

    const currentItem = items[currentIndex];
    const totalItems = items.length;
    const answeredMap = items.reduce((acc, item, idx) => ({
        ...acc,
        [idx]: !!answers[item.id]
    }), {});

    const [middlePhotoTriggered, setMiddlePhotoTriggered] = useState(false);
    const [middlePhotoInProgress, setMiddlePhotoInProgress] = useState(false);

    useEffect(() => {
        if (facePhoto2Taken || middlePhotoTriggered || !onMiddlePhotoNeeded) {
            return;
        }

        const progress = ((currentIndex + 1) / totalItems) * 100;

        if (progress >= 45 && progress < 55) {
            console.log('📸 Triggering middle photo at', progress, '%');
            setMiddlePhotoTriggered(true);
            setMiddlePhotoInProgress(true);

            showInfo(t('test.taking_photo') || '📸 Taking verification photo...');

            const shouldCapture = onMiddlePhotoNeeded();
            console.log('📸 Middle photo capture triggered:', shouldCapture);

            if (!shouldCapture) {
                setMiddlePhotoInProgress(false);
            }
        }
    }, [currentIndex, totalItems, facePhoto2Taken, middlePhotoTriggered, onMiddlePhotoNeeded, language]);

    useEffect(() => {
        if (facePhoto2Taken) {
            console.log('📸 Photo 2 taken - resuming test immediately');
            setMiddlePhotoTriggered(true);
            setMiddlePhotoInProgress(false);
        }
    }, [facePhoto2Taken]);

    useEffect(() => {
        if (facePhoto2Taken && middlePhotoTriggered) {
            setMiddlePhotoInProgress(false);
        }
    }, [facePhoto2Taken, middlePhotoTriggered]);

    useEffect(() => {
        if (savedProgress) {
            console.log('📂 Loading saved progress:', savedProgress);
            setAnswers(savedProgress.answers || {});
            setCurrentIndex(savedProgress.currentIndex || 0);
            setCompletedItems(savedProgress.completedItems || []);
            setIsResumed(true);

            if (savedProgress.answers && savedProgress.answers[items[savedProgress.currentIndex]?.id]) {
                setSelectedOption(savedProgress.answers[items[savedProgress.currentIndex]?.id]);
            }
        }
    }, [savedProgress, items]);

    const batchSaveResponses = async () => {
        if (pendingSaves.current.size === 0) return;
        const savesToSend = Array.from(pendingSaves.current.entries());
        setSaveStatus(prev => ({ ...prev, saving: true }));
        try {
            for (const [itemId, { value, responseTime }] of savesToSend) {
                await saveResponse(accessToken, {
                    item_id: itemId,
                    response_value: value,
                    response_time_ms: responseTime
                });
                setSaveStatus(prev => ({ ...prev, [itemId]: 'saved' }));
            }
            pendingSaves.current.clear();
        } catch (error) {
            console.error('Batch save failed:', error);
        } finally {
            setSaveStatus(prev => ({ ...prev, saving: false }));
        }
    };

    const saveProgressToServer = async () => {
        try {
            const response = await saveProgressAPI(accessToken, {
                answers: answers,
                currentIndex: currentIndex,
                completedItems: completedItems
            });
            if (response.data.success) {
                console.log('✅ Progress saved to server');
            }
        } catch (error) {
            console.error('❌ Failed to save progress:', error);
        }
    };

    useEffect(() => {
        if (progressSaveTimeout.current) {
            clearTimeout(progressSaveTimeout.current);
        }
        progressSaveTimeout.current = setTimeout(() => {
            if (Object.keys(answers).length > 0 || currentIndex > 0) {
                saveProgressToServer();
            }
        }, 2000);
        return () => {
            if (progressSaveTimeout.current) {
                clearTimeout(progressSaveTimeout.current);
            }
        };
    }, [answers, currentIndex, completedItems]);

    useEffect(() => {
        return () => {
            if (Object.keys(answers).length > 0 || currentIndex > 0) {
                saveProgressToServer();
            }
            if (saveTimeout.current) {
                clearTimeout(saveTimeout.current);
                batchSaveResponses();
            }
        };
    }, []);

    const debouncedSave = (itemId, value, responseTime) => {
        pendingSaves.current.set(itemId, { value, responseTime });
        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        saveTimeout.current = setTimeout(() => {
            batchSaveResponses();
        }, 500);
    };

    // Anti-cheating
    useEffect(() => {
        const handleBlur = () => {
            return false;
            setBlurCount(prev => prev + 1);
            addBlurEvent(accessToken, new Date().toISOString())
                .catch(console.error);
        };
        window.addEventListener('blur', handleBlur);
        return () => window.removeEventListener('blur', handleBlur);
    }, [accessToken]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            const isCurrentAnswered = !!answers[currentItem?.id];

            if (e.key === 'ArrowLeft' && currentIndex > 0) {
                goToPrevious();
            } else if (e.key === 'ArrowRight' && currentIndex < totalItems - 1) {
                if (!isCurrentAnswered) {
                    showWarning('⚠️ ' + (t('test.please_answer') || 'Please answer this question before proceeding.'));
                    return;
                }
                goToNext();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, totalItems, answers, currentItem, language]);

    const handleAnswer = (value) => {
        // ← Handle reset: value === null
        if (value === null) {
            setAnswers(prev => {
                const next = { ...prev };
                delete next[currentItem.id];
                return next;
            });
            setCompletedItems(prev => prev.filter(id => id !== currentItem.id));
            setSaveStatus(prev => ({ ...prev, [currentItem.id]: undefined }));
            return;
        }

        const startTime = Date.now();

        setAnswers(prev => ({ ...prev, [currentItem.id]: value }));
        setSelectedOption(value);
        setCompletedItems(prev => {
            if (!prev.includes(currentItem.id)) {
                return [...prev, currentItem.id];
            }
            return prev;
        });

        const responseTime = Date.now() - startTime;
        debouncedSave(currentItem.id, value, responseTime);
        setSaveStatus(prev => ({ ...prev, [currentItem.id]: 'saving' }));

        if (currentIndex < totalItems - 1) {
            setTimeout(() => {
                setCurrentIndex(prev => prev + 1);
                setSelectedOption(answers[items[currentIndex + 1]?.id] || null);
            }, 300);
        }
    };

    const handleOptionSelect = (itemId, option) => {
        handleAnswer(option);
    };

    const handleSubmit = async () => {
        const unanswered = items.filter(item => !answers[item.id]);
        if (unanswered.length > 0) {
            showWarning('⚠️ ' + (t('test.please_answer_all') || 'Please answer all questions before submitting.') + ` (${unanswered.length} ${t('test.unanswered') || 'remaining'})`);
            return;
        }

        if (isSubmitting) return;

        await saveProgressToServer();
        if (pendingSaves.current.size > 0) {
            await batchSaveResponses();
        }

        setIsSubmitting(true);
        await onSubmit();
    };

    const goToPrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setSelectedOption(answers[items[currentIndex - 1]?.id] || null);
        }
    };

    const goToNext = () => {
        const isCurrentAnswered = !!answers[currentItem?.id];

        if (!isCurrentAnswered) {
            showWarning('⚠️ ' + (t('test.please_answer') || 'Please answer this question before proceeding.'));
            return;
        }

        if (currentIndex < totalItems - 1) {
            setCurrentIndex(prev => prev + 1);
            setSelectedOption(answers[items[currentIndex + 1]?.id] || null);
        }
    };

    const handleDotNavigation = (index) => {
        if (index === currentIndex) return;

        if (answers[items[index]?.id]) {
            setCurrentIndex(index);
            setSelectedOption(answers[items[index]?.id] || null);
            return;
        }

        if (index > currentIndex) {
            const isCurrentAnswered = !!answers[currentItem?.id];
            if (!isCurrentAnswered) {
                showWarning('⚠️ ' + (t('test.please_answer') || 'Please answer the current question first.'));
                return;
            }
        }

        if (index < currentIndex && items[index]?.id) {
            if (items[index]) {
                setCurrentIndex(index);
                setSelectedOption(answers[items[index]?.id] || null);
            }
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft' && currentIndex > 0) {
                goToPrevious();
            } else if (e.key === 'ArrowRight' && currentIndex < totalItems - 1) {
                const isCurrentAnswered = !!answers[currentItem?.id];
                if (!isCurrentAnswered) {
                    showWarning(t('test.please_answer') || 'Please select option(s) before proceeding.');
                    return;
                }
                goToNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, totalItems, answers, currentItem, language]);

    // ============================================
    // EBCSS VIDEO
    // ============================================
    const renderVideoQuestion = () => {
        if (!currentItem) return null;

        return (
            <div className="flex-1 flex flex-col min-h-0 p-1">
                <VideoQuestion
                    key={currentItem.id}
                    currentItem={currentItem}
                    accessToken={accessToken}
                    onAnswerComplete={handleAnswer}
                    currentIndex={currentIndex}
                    totalQuestions={items.length}
                    answeredCount={Object.keys(answers).length}
                    onSubmitAssessment={onSubmit}
                    autoSubmitDelay={2000}
                />
            </div>
        );
    };

    // ============================================
    // CPM
    // ============================================
    const getCPMImagePath = (itemId) => {
        const imageId = itemId.replace('CPM-', '').trim();
        return `/images/cpm/${imageId}.png`;
    };

    const renderDefaultCPMImage = (itemId) => {
        const imagePath = getCPMImagePath(itemId);
        return (
            <div className="mb-4 flex justify-center">
                <img
                    src={imagePath}
                    alt={`Pattern ${itemId}`}
                    className="max-w-full max-h-80 object-contain border border-gray-200 rounded-lg"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        const parent = e.target.parentElement;
                        const fallback = document.createElement('div');
                        fallback.className = 'w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300';
                        fallback.innerHTML = `
                            <div class="text-center">
                                <p class="text-gray-500">${t('test.image_unavailable') || 'Image not available'}</p>
                                <p class="text-sm text-gray-600">${itemId}</p>
                            </div>
                        `;
                        parent.appendChild(fallback);
                    }}
                />
            </div>
        );
    };

    const renderDefaultCPMOptions = (item, selectedOption, handleOptionSelect, answers) => {
        const options = ['1', '2', '3', '4', '5', '6'];
        return (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 max-w-2xl mx-auto">
                {options.map((opt) => (
                    <button
                        key={opt}
                        onClick={() => handleOptionSelect(item.id, opt)}
                        className={`
                            p-3 rounded-lg border-2 transition-all duration-200 text-center
                            ${selectedOption === opt || answers[item.id] === opt
                                ? 'border-[#ff9f2f] bg-[#ff9f2f] bg-opacity-10 shadow-md'
                                : 'border-gray-200 hover:border-[#ff9f2f] hover:bg-gray-50'
                            }
                        `}
                    >
                        <div className="text-lg font-bold text-gray-700">{opt}</div>
                    </button>
                ))}
            </div>
        );
    };

    const renderCPMQuestion = () => {
        if (!currentItem) return null;

        const renderImage = renderCPMImage || renderDefaultCPMImage;
        const renderOptions = renderCPMOptions || renderDefaultCPMOptions;

        return (
            <div className="flex flex-col items-center justify-center w-full">
                {renderImage(currentItem.id)}
                <p className="text-base text-center text-gray-600 mb-4">
                    {currentItem.text || t('test.cpm_instruction') || 'Select the option that completes the pattern'}
                </p>
                {renderOptions(currentItem, selectedOption, handleOptionSelect, answers)}
            </div>
        );
    };

    // ============================================
    // LIKERT
    // ============================================
    const renderLikertQuestion = () => {
        if (!currentItem) return null;

        const responseOptions = currentItem.response_options || [];

        let options = [];
        if (responseOptions.length > 0) {
            options = responseOptions.map(opt => ({
                value: opt.value,
                score: opt.score,
                display: opt.value
            }));
        } else {
            const responseFormat = currentItem.response_format || '';
            if (responseFormat && responseFormat.includes(',')) {
                options = responseFormat.split(',').map(opt => ({
                    value: opt.trim(),
                    score: 0,
                    display: opt.trim()
                }));
            } else {
                options = [
                    { value: 'True', score: 1, display: t('common.yes') || 'True' },
                    { value: 'False', score: 0, display: t('common.no') || 'False' }
                ];
            }
        }

        return (
            <div className="flex flex-col items-center justify-center w-full max-w-2xl mx-auto">
                <p className="text-lg text-center text-gray-800 mb-6">
                    {currentItem.text}
                </p>
                <div className="space-y-3 w-full">
                    {options.map((option, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleAnswer(option.value)}
                            className={`
                                w-full p-3 text-left border rounded-lg transition-all
                                ${answers[currentItem.id] === option.value
                                    ? 'bg-[#ff9f2f] text-white border-[#ff9f2f]'
                                    : 'hover:bg-gray-50 border-gray-200'
                                }
                            `}
                        >
                            <span>{option.display}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    const renderQuestion = () => {
        console.log('🎯 Rendering question type:', { assessmentType, isCPM, itemId: currentItem?.id });

        if (isCPM || assessmentType === 'CPM') {
            return renderCPMQuestion();
        }

        if (assessmentType === 'EBCSS') {
            return renderVideoQuestion();
        }

        return renderLikertQuestion();
    };

    if (!currentItem) {
        return (
            <div className="text-center py-8 text-gray-600">
                {t('common.loading') || 'Loading questions...'}
            </div>
        );
    }

    const progress = ((currentIndex + 1) / totalItems) * 100;
    const isAnswered = !!answers[currentItem?.id];

    const getAssessmentName = () => {
        if (isCPM || assessmentType === 'CPM') return 'CPM';
        if (assessmentType === 'GHQ') return 'GHQ';
        if (assessmentType === 'EBCSS') return t('test.assessment_name_ebcss') || 'EBCSS (Video)';
        if (assessmentType === 'MIPS') return 'MIPS';
        return 'Assessment';
    };

    return (
        <div className="min-h-screen bg-[#f4f6fb] flex flex-col" ref={containerRef}>
            {/* Header */}
            <header className="bg-[#063b56] text-white py-2 px-4 flex justify-between items-center shadow-sm flex-shrink-0">
                <div className="flex items-center space-x-3">
                    <h1 className="text-sm font-semibold">{getAssessmentName()}</h1>
                </div>
                <div className="flex items-center space-x-3">
                    <span className="text-xs text-gray-300">
                        {t('test.answered_count', {
                            answered: Object.keys(answers).length,
                            total: totalItems
                        }) || `${Object.keys(answers).length} of ${totalItems} answered`}
                    </span>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 flex flex-col relative overflow-hidden">
                {/* Question Content */}
                <div className="flex-1 flex flex-col items-center justify-center px-0 md:px-20 py-2 h-full">
                    <div className="w-full max-w-3xl mx-auto h-full flex flex-col">
                        <div className="flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 p-0 md:p-6 overflow-y-auto">
                            {renderQuestion()}
                        </div>

                        {/* Submit Button - Only on last question */}
                        {currentIndex === totalItems - 1 && (
                            <div className="mt-3 text-center flex-shrink-0">
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="px-8 py-3 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a]
                            disabled:opacity-50 shadow-sm transition-all duration-200 font-medium text-sm"
                                >
                                    {isSubmitting
                                        ? (t('test.submitting') || 'Submitting...')
                                        : (t('test.submit') || 'Submit Assessment')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ============================================ */}
                {/* PREVIOUS BUTTON — side on desktop, bottom-left on mobile */}
                {/* ============================================ */}
                {currentIndex > 0 && (
                    <button
                        onClick={goToPrevious}
                        className="
                /* Mobile: fixed to bottom-left, above the dots */
                absolute
                bottom-0 left-3
                /* Desktop: side-middle */
                md:bottom-auto md:left-4 md:top-1/2 md:-translate-y-1/2
                /* Common sizing */
                w-11 h-11 md:w-12 md:h-12
                rounded-full bg-white shadow-lg hover:bg-gray-50
                border border-gray-200 flex items-center justify-center
                transition-all duration-200 hover:shadow-xl z-30
            "
                        aria-label={t('common.back') || 'Previous question'}
                    >
                        <ChevronLeftIcon className="h-5 w-5 md:h-6 md:w-6 text-[#063b56]" />
                    </button>
                )}

                {/* ============================================ */}
                {/* NEXT BUTTON — side on desktop, bottom-right on mobile */}
                {/* ============================================ */}
                {currentIndex < totalItems - 1 && (
                    <button
                        onClick={goToNext}
                        className="
                /* Mobile: fixed to bottom-right, above the dots */
                absolute
                bottom-0 right-3
                /* Desktop: side-middle */
                md:bottom-auto md:right-4 md:top-1/2 md:-translate-y-1/2
                /* Common sizing */
                w-11 h-11 md:w-12 md:h-12
                rounded-full bg-white shadow-lg hover:bg-gray-50
                border border-gray-200 flex items-center justify-center
                transition-all duration-200 hover:shadow-xl z-30
            "
                        aria-label={t('common.next') || 'Next question'}
                    >
                        <ChevronRightIcon className="h-5 w-5 md:h-6 md:w-6 text-[#063b56]" />
                    </button>
                )}
            </div>

            {/* Bottom Navigation Dots */}
            <div className="py-2 px-4 flex justify-center flex-shrink-0">
                <QuestionNav
                    total={totalItems}
                    current={currentIndex}
                    answered={answeredMap}
                    onSelect={handleDotNavigation}
                />
            </div>

            {/* Blur Warning */}
            {blurCount > 0 && (
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2
                    px-4 py-2 bg-[#ff9f2f] text-white text-xs rounded-lg shadow-lg z-50">
                    ⚠️ {t('test.stay_on_tab') || 'Stay on this tab during the assessment'}
                    <button
                        onClick={() => setBlurCount(0)}
                        className="ml-3 text-white hover:text-gray-200 focus:outline-none"
                    >
                        ✕
                    </button>
                </div>
            )}
        </div>
    );
};

export default TestInterface;